import { useMemo } from 'react';
import { useT } from '../i18n';

// Vocabulaire de personnalisation du mini-site de mariage : ce que l'éditeur propose
// (MinisiteEditor.js) et ce que le rendu sait afficher (MinisiteView.js). Les clés sont celles
// stockées dans cartes_v2.params ; côté serveur, minisiteSanitizeParams() (apiEtsy.php) borne
// les valeurs mais ne connaît pas ces listes : une clé inconnue retombe sur la valeur par défaut.

// `google` : famille à charger depuis Google Fonts ; absent = déjà chargée par public/index.html.
export const MINISITE_FONTS = [
  { key: 'parisienne', label: 'Parisienne', family: "'Parisienne', cursive", style: 'script' },
  { key: 'great-vibes', label: 'Great Vibes', family: "'Great Vibes', cursive", style: 'script', google: 'Great+Vibes' },
  { key: 'allura', label: 'Allura', family: "'Allura', cursive", style: 'script', google: 'Allura' },
  { key: 'pinyon', label: 'Pinyon Script', family: "'Pinyon Script', cursive", style: 'script', google: 'Pinyon+Script' },
  { key: 'dancing', label: 'Dancing Script', family: "'Dancing Script', cursive", style: 'script', google: 'Dancing+Script:wght@400;600' },
  { key: 'neucha', label: 'Neucha', family: "'Neucha', cursive", style: 'script' },
  { key: 'cormorant', label: 'Cormorant Garamond', family: "'Cormorant Garamond', serif", style: 'serif' },
  { key: 'playfair', label: 'Playfair Display', family: "'Playfair Display', serif", style: 'serif', google: 'Playfair+Display:wght@400;600' },
  { key: 'cinzel', label: 'Cinzel', family: "'Cinzel', serif", style: 'serif', google: 'Cinzel:wght@400;600' },
  { key: 'josefin', label: 'Josefin Sans', family: "'Josefin Sans', sans-serif", style: 'sans', google: 'Josefin+Sans:wght@300;400;600' },
  { key: 'dm-sans', label: 'DM Sans', family: "'DM Sans', sans-serif", style: 'sans' },
];

// Polices proposées pour le texte courant : lisibles en petit corps, pas de script.
export const MINISITE_TEXT_FONT_KEYS = ['dm-sans', 'josefin', 'cormorant', 'playfair'];

export const MINISITE_COLORS = [
  { key: 'rose-poudre', label: 'Rose poudré', hex: '#c48f8b' },
  { key: 'terracotta', label: 'Terracotta', hex: '#c4704f' },
  { key: 'bordeaux', label: 'Bordeaux', hex: '#7a2e3a' },
  { key: 'or', label: 'Or', hex: '#b8935a' },
  { key: 'vert-sauge', label: 'Vert sauge', hex: '#7cb98a' },
  { key: 'eucalyptus', label: 'Eucalyptus', hex: '#5f8575' },
  { key: 'lavande', label: 'Lavande', hex: '#8C8BC4' },
  { key: 'bleu-nuit', label: 'Bleu nuit', hex: '#2c3e5c' },
  { key: 'brun-intemporel', label: 'Brun', hex: '#3a2b2b' },
  { key: 'noir', label: 'Noir', hex: '#222222' },
  { key: 'blanc', label: 'Blanc', hex: '#ffffff' },
];

export const MINISITE_ANIMATIONS = [
  { key: 'douce', label: 'Apparition douce' },
  { key: 'lettres', label: 'Lettre par lettre' },
  { key: 'zoom', label: 'Zoom arrière' },
  { key: 'fondu', label: 'Fondu lent' },
];

export const MINISITE_AMBIANCES = [
  { key: 'claire', label: 'Claire' },
  { key: 'sombre', label: 'Sombre' },
];

export const MINISITE_DISPOSITIONS = [
  { key: 'haut', label: 'En haut' },
  { key: 'centre', label: 'Au centre' },
  { key: 'bas', label: 'En bas' },
];

const HEX = /^#[0-9a-fA-F]{6}$/;
const findFont = (key) => MINISITE_FONTS.find((f) => f.key === key);

export const resolveMinisiteFont = (key, fallbackKey = 'parisienne') =>
  (findFont(key) || findFont(fallbackKey)).family;

// `couleur` est soit la clé d'une couleur prédéfinie, soit une couleur libre (#rrggbb).
export const resolveMinisiteColor = (couleur) => {
  if (HEX.test(couleur || '')) return couleur;
  return (MINISITE_COLORS.find((c) => c.key === couleur) || MINISITE_COLORS[0]).hex;
};

// Feuille de style Google Fonts pour les polices demandées qui ne sont pas déjà chargées par
// public/index.html ; null si rien à charger. Sans argument : toutes (sélecteur de l'éditeur).
export const minisiteFontsHref = (keys) => {
  const fonts = keys ? keys.map(findFont).filter(Boolean) : MINISITE_FONTS;
  const families = [...new Set(fonts.map((f) => f.google).filter(Boolean))];
  if (families.length === 0) return null;
  return `https://fonts.googleapis.com/css2?${families.map((f) => `family=${f}`).join('&')}&display=swap`;
};

// Libellés traduits (éditeur de l'acheteur) : mêmes listes, mêmes clés, `label` dans la langue courante ; les noms
// de polices ne se traduisent pas. Dictionnaire : minisite-occasions.json, `theme.colors.<clé>`, `.animations`,
// `.ambiances`, `.dispositions`. Clé absente = libellé français ci-dessus.
export const useMinisiteTheme = () => {
  const t = useT('minisite-occasions');
  return useMemo(() => {
    const labels = (list, group) => list.map((item) => ({ ...item, label: t.or(`minisite-occasions.theme.${group}.${item.key}`, item.label) }));
    return {
      colors: labels(MINISITE_COLORS, 'colors'),
      animations: labels(MINISITE_ANIMATIONS, 'animations'),
      ambiances: labels(MINISITE_AMBIANCES, 'ambiances'),
      dispositions: labels(MINISITE_DISPOSITIONS, 'dispositions'),
      fonts: MINISITE_FONTS,
    };
  }, [t]);
};
